import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  MEMBER_PHOTO_ALLOWED_TYPES,
  MEMBER_PHOTO_MAX_SIZE,
  resolveMemberPhotoUrl,
} from '@/lib/memberPhoto';
import {
  useClinicUser,
  useMemberExceptions,
  useUpdateMemberProfile,
  useUpdateMemberUserName,
  useSetMemberWorkingHours,
  useChangeMemberRole,
  useRemoveClinicMembership,
  useRestoreClinicMembership,
  useAddMemberException,
  useDeleteMemberException,
  readWorkingHours,
  DEFAULT_WORKING_HOURS,
  type WorkingHours,
  type WeekDayHours,
  type ExceptionType,
} from '@/hooks/useClinicUsers';
import { useAssignMemberCustomRole, useClinicRoles } from '@/hooks/useClinicRoles';
import { clinicRoleDisplayName } from '@/lib/clinicRoleLabel';
import { journalPatientConfirmState } from '@/lib/journalPatientConfirm';
import { memberRoleIsPractitioner } from '@/lib/practitionerRole';
import { useUpdateOwnEmailSignature } from '@/hooks/useClinicEmail';
import { containsCpr, SIGNATURE_TEXT_MAX } from '@/lib/clinicEmail/catalog';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TimeInput } from '@/components/ui/TimeInput';
import { SectionCard } from './ClinicGeneral';
import { SettingsLoading } from './settingsChrome';
import { Avatar, RoleBadge } from './ClinicUsers';
import { PractitionerCompensation } from './PractitionerCompensation';

const WEEKDAYS: Array<keyof WorkingHours> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const EXCEPTION_TYPES: ExceptionType[] = [
  'unavailable',
  'custom_hours',
  'meeting',
  'vacation',
  'sick',
];

export function ClinicUserDetail() {
  const { id: membershipId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission, user: authUser } = useAuth();

  const { data: member, isLoading, error } = useClinicUser(membershipId);
  const { data: exceptions } = useMemberExceptions(membershipId);

  const updateProfile = useUpdateMemberProfile(activeClinicId);
  const updateUserName = useUpdateMemberUserName(activeClinicId);
  const setHours = useSetMemberWorkingHours(activeClinicId);
  const changeRole = useChangeMemberRole(activeClinicId);
  const assignCustomRole = useAssignMemberCustomRole(activeClinicId);
  const removeMembership = useRemoveClinicMembership(activeClinicId);
  const restoreMembership = useRestoreClinicMembership(activeClinicId);
  const { data: clinicRoles, isLoading: rolesLoading } = useClinicRoles(activeClinicId);

  const canManageUsers = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_users', 'update'),
  );
  const isSelf = member?.user_id === authUser?.id;
  const canEditProfile = canManageUsers || isSelf;
  const canEditRole = canManageUsers && !isSelf;
  const customRoles = (clinicRoles ?? []).filter((r) => !r.is_system || r.system_key === 'employee');

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [color, setColor] = useState('#6366F1');
  const [bio, setBio] = useState('');
  const [specializationsText, setSpecializationsText] = useState('');
  const [qualificationsText, setQualificationsText] = useState('');
  const [experience, setExperience] = useState('');
  const [showInBooking, setShowInBooking] = useState(true);
  const [confirmJournalPatient, setConfirmJournalPatient] = useState(false);
  const [signatureText, setSignatureText] = useState('');

  const [hours, setHoursState] = useState<WorkingHours>(DEFAULT_WORKING_HOURS);
  const updateOwnSignature = useUpdateOwnEmailSignature();

  useEffect(() => {
    if (!member) return;
    setName(member.user?.name?.trim() || '');
    setTitle(member.title ?? '');
    setPhone(member.phone ?? '');
    setPhotoUrl(member.photo_url ?? '');
    setPhotoFile(null);
    setPhotoPreview(resolveMemberPhotoUrl(member.photo_url) ?? '');
    setColor(member.color);
    setBio(member.bio ?? '');
    setSpecializationsText((member.specializations ?? []).join(', '));
    setQualificationsText((member.qualifications ?? []).join(', '));
    setExperience(member.experience ?? '');
    setShowInBooking(member.show_in_booking);
    setConfirmJournalPatient(Boolean(member.confirm_journal_patient));
    setSignatureText(member.email_signature_text ?? '');
    setHoursState(readWorkingHours(member.working_hours));
    if (photoInputRef.current) photoInputRef.current.value = '';
  }, [member]);

  if (isLoading || rolesLoading) {
    return <SettingsLoading />;
  }
  if (error) return <ErrorAlert message={t('users.load_error')} />;
  if (!member) return <ErrorAlert message={t('users.load_error')} />;

  const userName = name.trim() || member.user?.name?.trim() || member.user?.email || '—';
  const journalPatientConfirm = journalPatientConfirmState(
    {
      role: member.role,
      custom_role_id: member.custom_role_id,
      confirm_journal_patient: confirmJournalPatient,
    },
    clinicRoles ?? [],
  );
  const journalPatientConfirmForced = journalPatientConfirm.forcedByRole;
  const isInactive = member.status === 'inactive';
  const savingProfile = updateProfile.isPending || updateUserName.isPending || uploadingPhoto;
  const displayPhotoUrl = photoPreview || resolveMemberPhotoUrl(photoUrl);

  function handlePhotoFile(file: File | null) {
    if (!file) {
      setPhotoFile(null);
      return;
    }
    if (!(MEMBER_PHOTO_ALLOWED_TYPES as readonly string[]).includes(file.type)) {
      toast.error(t('users.errors.invalid_photo_type'));
      return;
    }
    if (file.size > MEMBER_PHOTO_MAX_SIZE) {
      toast.error(t('users.errors.photo_too_large'));
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleRemovePhoto() {
    setPhotoFile(null);
    setPhotoPreview('');
    setPhotoUrl('');
    if (photoInputRef.current) photoInputRef.current.value = '';
  }

  async function onSubmitProfile(e: FormEvent) {
    e.preventDefault();
    if (!canEditProfile || !membershipId || !activeClinicId) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t('users.errors.invalid_name'));
      return;
    }
    const trimmedTitle = title.trim();
    if (trimmedTitle.length > 35) {
      toast.error(t('users.errors.title_too_long', 'Titel må højst være 35 tegn.'));
      return;
    }
    try {
      const currentName = member?.user?.name?.trim() || '';
      if (trimmedName !== currentName) {
        await updateUserName.mutateAsync({
          membership_id: membershipId,
          name: trimmedName,
        });
      }

      let finalPhotoUrl = photoUrl.trim();
      if (photoFile) {
        setUploadingPhoto(true);
        const ext = photoFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const storagePath = `${activeClinicId}/${membershipId}/photo.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('member-photos')
          .upload(storagePath, photoFile, {
            upsert: true,
            contentType: photoFile.type,
          });
        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }
        finalPhotoUrl = `member-photos/${storagePath}`;
        setPhotoUrl(finalPhotoUrl);
        setPhotoPreview(resolveMemberPhotoUrl(finalPhotoUrl) ?? '');
        setPhotoFile(null);
        if (photoInputRef.current) photoInputRef.current.value = '';
        setUploadingPhoto(false);
      }

      await updateProfile.mutateAsync({
        membership_id: membershipId,
        title: trimmedTitle || null,
        phone: phone.trim() || null,
        // Empty string clears the photo (COALESCE keeps prior value for null/omitted).
        photo_url: finalPhotoUrl,
        color,
        bio: bio.trim() || null,
        specializations: parseList(specializationsText),
        qualifications: parseList(qualificationsText),
        experience: experience.trim() || null,
        show_in_booking: showInBooking,
        // Personal preference only. Role force must not overwrite the stored value.
        confirm_journal_patient: confirmJournalPatient,
      });
      toast.success(t('users.toast_profile_updated'));
    } catch (err) {
      setUploadingPhoto(false);
      toast.error(translateError(t, err));
    }
  }

  async function onSubmitHours(e: FormEvent) {
    e.preventDefault();
    if (!canEditProfile || !membershipId) return;
    for (const day of WEEKDAYS) {
      const d = hours[day];
      if (d.enabled && d.start >= d.end) {
        toast.error(t('users.errors.invalid_hours'));
        return;
      }
    }
    try {
      await setHours.mutateAsync({
        membership_id: membershipId,
        working_hours: hours,
      });
      toast.success(t('users.toast_hours_updated'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onRemove() {
    if (!canManageUsers || !membershipId) return;
    if (!window.confirm(t('users.confirm_remove_membership'))) return;
    try {
      await removeMembership.mutateAsync(membershipId);
      toast.success(t('users.toast_member_removed'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onRestore() {
    if (!canManageUsers || !membershipId) return;
    if (!window.confirm(t('users.confirm_restore_membership'))) return;
    try {
      await restoreMembership.mutateAsync(membershipId);
      toast.success(t('users.toast_member_restored'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/settings/users"
          className="inline-flex items-center text-sm text-ds-text hover:text-ds-teal"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('users.back_to_list')}
        </Link>
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  ">
        <Avatar name={userName} color={color} photoUrl={displayPhotoUrl} size="lg" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-ds-text">{userName}</h2>
          <p className="text-sm text-ds-muted">{member.user?.email}</p>
          <div className="mt-2 flex items-center gap-2">
            <RoleBadge
              role={member.role}
              customRoleId={member.custom_role_id}
              clinicRoles={clinicRoles ?? []}
            />
            {isInactive && (
              <span className="inline-flex items-center rounded-full bg-ds-neutral-tint px-2 py-0.5 text-xs font-medium text-ds-muted  ">
                {t('users.status_inactive')}
              </span>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={onSubmitProfile} className="space-y-6">
        <SectionCard title={t('users.section_profile')} readOnly={!canEditProfile}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PlainField
              label={t('users.field_name')}
              value={name}
              onChange={setName}
              disabled={!canEditProfile}
            />
            <PlainField
              label={t('users.field_title')}
              value={title}
              onChange={setTitle}
              disabled={!canEditProfile}
              maxLength={35}
              hint={t(
                'users.field_title_hint',
                'Stillingsbetegnelse (højst 35 tegn). Bruges på billeddiagnostiske henvisninger.',
              )}
            />
            <PlainField
              label={t('users.field_phone')}
              value={phone}
              onChange={setPhone}
              disabled={!canEditProfile}
            />
            <ColorField
              label={t('users.field_color')}
              value={color}
              onChange={setColor}
              disabled={!canEditProfile}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-ds-text mb-2">
                {t('users.field_photo')}
              </label>
              <div className="space-y-3">
                <div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept={MEMBER_PHOTO_ALLOWED_TYPES.join(',')}
                    onChange={(e) => handlePhotoFile(e.target.files?.[0] || null)}
                    disabled={!canEditProfile}
                    className="block w-full text-sm text-ds-muted file:mr-4 file:rounded-xl file:border-0 file:bg-ds-teal-tint file:px-4 file:py-2 file:text-sm file:font-semibold file:text-ds-teal hover:file:bg-ds-teal-tint disabled:opacity-60"
                  />
                  <p className="mt-1 text-xs text-ds-muted">
                    {t('users.field_photo_hint')}
                  </p>
                </div>
                {photoPreview && (
                  <div className="relative inline-block">
                    <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border border-ds-field bg-ds-card  ">
                      <img
                        src={photoPreview}
                        alt={userName}
                        className="max-h-full max-w-full object-cover"
                      />
                    </div>
                    {canEditProfile && (
                      <Button
                        type="button"
                        variant="danger"
                        size="icon"
                        className="absolute -right-2 -top-2"
                        onClick={handleRemovePhoto}
                        aria-label={t('common.delete')}
                      >
                        <X />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-ds-text">
                {t('users.field_bio')}
              </label>
              <textarea
                rows={3}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                disabled={!canEditProfile}
                className="mt-1 block w-full rounded-xl border border-ds-field bg-ds-card px-3 py-2 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-60   "
              />
            </div>
            <PlainField
              label={`${t('users.field_specializations')} — ${t('users.comma_hint')}`}
              value={specializationsText}
              onChange={setSpecializationsText}
              disabled={!canEditProfile}
            />
            <PlainField
              label={`${t('users.field_qualifications')} — ${t('users.comma_hint')}`}
              value={qualificationsText}
              onChange={setQualificationsText}
              disabled={!canEditProfile}
            />
            <div className="md:col-span-2">
              <PlainField
                label={t('users.field_experience')}
                value={experience}
                onChange={setExperience}
                disabled={!canEditProfile}
              />
            </div>
            <div className="md:col-span-2">
              <label className="inline-flex items-start gap-3 text-sm text-ds-text">
                <input
                  type="checkbox"
                  checked={showInBooking}
                  onChange={(e) => setShowInBooking(e.target.checked)}
                  disabled={!canEditProfile}
                  className="mt-0.5 h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring"
                />
                <span>
                  <span className="block font-medium">{t('users.field_show_in_booking')}</span>
                  <span className="text-xs text-ds-muted">
                    {t('users.field_show_in_booking_desc')}
                  </span>
                </span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="inline-flex items-start gap-3 text-sm text-ds-text">
                <input
                  type="checkbox"
                  checked={journalPatientConfirmForced || confirmJournalPatient}
                  onChange={(e) => {
                    if (journalPatientConfirmForced) return;
                    setConfirmJournalPatient(e.target.checked);
                  }}
                  disabled={!canEditProfile || journalPatientConfirmForced}
                  className="mt-0.5 h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring disabled:opacity-60"
                />
                <span>
                  <span className="block font-medium">{t('users.field_confirm_journal_patient')}</span>
                  <span className="text-xs text-ds-muted">
                    {journalPatientConfirmForced
                      ? t('users.field_confirm_journal_patient_forced_desc')
                      : t('users.field_confirm_journal_patient_desc')}
                  </span>
                </span>
              </label>
            </div>
          </div>
          {canEditProfile && (
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={savingProfile}>
                {savingProfile ? t('common.saving') : t('users.save_profile')}
              </Button>
            </div>
          )}
        </SectionCard>
      </form>

      {isSelf && membershipId && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (containsCpr(signatureText)) {
              toast.error(t('users.errors.signature_cpr'));
              return;
            }
            try {
              await updateOwnSignature.mutateAsync({
                membershipId,
                signatureText,
              });
              toast.success(t('users.signature_saved'));
            } catch (err) {
              toast.error(translateError(t, err));
            }
          }}
          className="space-y-6"
        >
          <SectionCard title={t('users.section_signature')}>
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('users.field_signature')}
              </label>
              <textarea
                rows={4}
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                maxLength={SIGNATURE_TEXT_MAX}
                className="mt-1 block w-full rounded-xl border border-ds-field bg-ds-card px-3 py-2 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-60"
              />
              <p className="mt-1 text-xs text-ds-muted">{t('users.field_signature_hint')}</p>
            </div>
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={updateOwnSignature.isPending}>
                {updateOwnSignature.isPending ? t('common.saving') : t('users.save_signature')}
              </Button>
            </div>
          </SectionCard>
        </form>
      )}

      {canEditRole && !isInactive && (
        <SectionCard title={t('users.section_role')}>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('users.field_role')}
            </label>
            <select
              value={
                member.role === 'main_clinic_admin'
                  ? 'main_clinic_admin'
                  : member.role === 'clinic_admin'
                    ? 'clinic_admin'
                    : member.custom_role_id
                      ? `role:${member.custom_role_id}`
                      : `role:${customRoles.find((r) => r.system_key === 'employee')?.id ?? ''}`
              }
              onChange={async (e) => {
                if (!membershipId) return;
                const value = e.target.value;
                try {
                  if (value === 'clinic_admin') {
                    if (member.role !== 'clinic_admin') {
                      await changeRole.mutateAsync({
                        membership_id: membershipId,
                        new_role: 'clinic_admin',
                      });
                    }
                    if (member.custom_role_id) {
                      await assignCustomRole.mutateAsync({
                        membershipId,
                        customRoleId: null,
                      });
                    }
                    toast.success(t('users.toast_role_updated'));
                    return;
                  }
                  if (value.startsWith('role:')) {
                    const roleId = value.slice('role:'.length);
                    if (member.role !== 'employee') {
                      await changeRole.mutateAsync({
                        membership_id: membershipId,
                        new_role: 'employee',
                      });
                    }
                    const employeeTemplate = customRoles.find((r) => r.system_key === 'employee');
                    const nextCustomId =
                      employeeTemplate && roleId === employeeTemplate.id ? null : roleId || null;
                    await assignCustomRole.mutateAsync({
                      membershipId,
                      customRoleId: nextCustomId,
                    });
                    toast.success(t('users.toast_role_updated'));
                  }
                } catch {
                  toast.error(t('users.toast_custom_role_error'));
                }
              }}
              disabled={changeRole.isPending || assignCustomRole.isPending}
              className="mt-1 block h-[44px] w-full rounded-xl border border-ds-field bg-ds-card px-3 text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-60   "
            >
              {member.role === 'main_clinic_admin' && (
                <option value="main_clinic_admin">{t('users.role_main_clinic_admin')}</option>
              )}
              <option value="clinic_admin">{t('users.role_clinic_admin')}</option>
              {customRoles.map((r) => (
                <option key={r.id} value={`role:${r.id}`}>
                  {clinicRoleDisplayName(r, (key) => t(key))}
                  {r.is_system ? ` (${t('roles.system_badge')})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ds-muted">{t('users.invite_role_hint')}</p>
          </div>
        </SectionCard>
      )}

      <form onSubmit={onSubmitHours} className="space-y-6">
        <SectionCard title={t('users.section_hours')} readOnly={!canEditProfile}>
          <div className="space-y-2">
            {WEEKDAYS.map((day) => (
              <DayRow
                key={day}
                day={day}
                value={hours[day]}
                onChange={(next) => setHoursState((prev) => ({ ...prev, [day]: next }))}
                disabled={!canEditProfile}
              />
            ))}
          </div>
          {canEditProfile && (
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={setHours.isPending}>
                {setHours.isPending ? t('common.saving') : t('users.save_hours')}
              </Button>
            </div>
          )}
        </SectionCard>
      </form>

      <ExceptionsSection
        membershipId={membershipId!}
        exceptions={exceptions ?? []}
        canEdit={canEditProfile}
      />

      {activeClinicId &&
        memberRoleIsPractitioner(member, clinicRoles ?? []) && (
          <PractitionerCompensation
            clinicId={activeClinicId}
            practitionerId={member.user_id}
            canEdit={canManageUsers}
          />
        )}

      {canManageUsers && !isSelf && (
        <SectionCard title={t('users.section_membership')}>
          <p className="mb-4 text-sm text-ds-muted">
            {isInactive
              ? t('users.confirm_restore_membership')
              : t('users.confirm_remove_membership')}
          </p>
          {isInactive ? (
            <Button type="button" onClick={onRestore} disabled={restoreMembership.isPending}>
              {t('users.restore_membership')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="danger"
              onClick={onRemove}
              disabled={removeMembership.isPending}
            >
              {removeMembership.isPending ? t('common.saving') : t('users.remove_membership')}
            </Button>
          )}
        </SectionCard>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (kept local to this page like other settings pages)
// ---------------------------------------------------------------------------

function PlainField({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
  maxLength,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: string;
  maxLength?: number;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ds-text">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        maxLength={maxLength}
        className="mt-1 block h-[44px] w-full rounded-xl border border-ds-field bg-ds-card px-3 text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-60"
      />
      {hint ? <p className="mt-1 text-xs text-ds-muted">{hint}</p> : null}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ds-text">{label}</label>
      <div className="mt-1 flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          disabled={disabled}
          className="h-11 w-16 cursor-pointer rounded-xl border border-ds-field bg-ds-card p-1 disabled:opacity-60  "
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="block h-[44px] w-32 rounded-xl border border-ds-field bg-ds-card px-3 font-mono text-sm uppercase text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-60   "
        />
      </div>
    </div>
  );
}

function DayRow({
  day,
  value,
  onChange,
  disabled,
}: {
  day: keyof WorkingHours;
  value: WeekDayHours;
  onChange: (next: WeekDayHours) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[140px_auto_1fr]">
      <label className="inline-flex items-center gap-2 text-sm font-medium text-ds-text ">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          disabled={disabled}
          className="h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring"
        />
        {t(`users.weekdays.${day}`)}
      </label>
      {value.enabled ? (
        <>
          <div className="flex items-center gap-2 text-sm">
            <TimeInput
              value={value.start}
              onChange={(start) => onChange({ ...value, start })}
              disabled={disabled}
            />
            <span className="text-ds-muted">{t('users.to')}</span>
            <TimeInput
              value={value.end}
              onChange={(end) => onChange({ ...value, end })}
              disabled={disabled}
            />
          </div>
          <div />
        </>
      ) : (
        <div className="text-sm text-ds-muted sm:col-span-2">
          {t('users.closed')}
        </div>
      )}
    </div>
  );
}

function ExceptionsSection({
  membershipId,
  exceptions,
  canEdit,
}: {
  membershipId: string;
  exceptions: Array<{
    id: string;
    exception_type: ExceptionType;
    starts_on: string;
    ends_on: string;
    starts_time: string | null;
    ends_time: string | null;
    reason: string | null;
  }>;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [exType, setExType] = useState<ExceptionType>('vacation');
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState(today);
  const [startsTime, setStartsTime] = useState<string>('');
  const [endsTime, setEndsTime] = useState<string>('');
  const [reason, setReason] = useState('');

  const addException = useAddMemberException();
  const deleteException = useDeleteMemberException();

  const needsTimes = exType === 'custom_hours';

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    try {
      await addException.mutateAsync({
        membership_id: membershipId,
        exception_type: exType,
        starts_on: startsOn,
        ends_on: endsOn,
        starts_time: startsTime || null,
        ends_time: endsTime || null,
        reason: reason.trim() || null,
      });
      toast.success(t('users.toast_exception_added'));
      setReason('');
      setStartsTime('');
      setEndsTime('');
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onDelete(id: string) {
    try {
      await deleteException.mutateAsync({ exception_id: id, membership_id: membershipId });
      toast.success(t('users.toast_exception_deleted'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <SectionCard title={t('users.section_exceptions')} readOnly={!canEdit}>
      {canEdit && (
        <form onSubmit={onAdd} className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.type')}
            </label>
            <select
              value={exType}
              onChange={(e) => setExType(e.target.value as ExceptionType)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
            >
              {EXCEPTION_TYPES.map((t1) => (
                <option key={t1} value={t1}>
                  {t(`users.exceptions.type_${t1}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.starts_on')}
            </label>
            <input
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.ends_on')}
            </label>
            <input
              type="date"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.starts_time')}
            </label>
            <div className="mt-1">
              <TimeInput
                value={startsTime}
                onChange={setStartsTime}
                required={needsTimes}
                allowEmpty={!needsTimes}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.ends_time')}
            </label>
            <div className="mt-1">
              <TimeInput
                value={endsTime}
                onChange={setEndsTime}
                required={needsTimes}
                allowEmpty={!needsTimes}
              />
            </div>
          </div>
          <div className="sm:col-span-5">
            <label className="block text-xs font-medium text-ds-text">
              {t('users.exceptions.reason')}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
            />
          </div>
          <div className="flex items-end">
            <Button type="submit" size="sm" disabled={addException.isPending} className="w-full">
              <Plus className="mr-1 h-4 w-4" />
              {t('users.exceptions.add')}
            </Button>
          </div>
        </form>
      )}

      {exceptions.length === 0 ? (
        <div className="rounded-md border border-dashed border-ds-field px-4 py-8 text-center text-sm text-ds-muted  ">
          {t('users.exceptions.no_exceptions')}
        </div>
      ) : (
        <ul className="divide-y divide-ds-line">
          {exceptions.map((ex) => (
            <li key={ex.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <ExceptionTypeBadge type={ex.exception_type} />
                  <span className="text-sm text-ds-text ">
                    {formatDateRange(ex.starts_on, ex.ends_on)}
                  </span>
                  {ex.starts_time && ex.ends_time ? (
                    <span className="text-xs text-ds-muted">
                      {ex.starts_time.slice(0, 5)} {t('users.to')} {ex.ends_time.slice(0, 5)}
                    </span>
                  ) : (
                    <span className="text-xs text-ds-muted">
                      {t('users.exceptions.all_day')}
                    </span>
                  )}
                </div>
                {ex.reason && (
                  <div className="mt-1 truncate text-xs text-ds-muted">
                    {ex.reason}
                  </div>
                )}
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="danger"
                  size="icon"
                  onClick={() => onDelete(ex.id)}
                  aria-label={t('common.delete')}
                >
                  <Trash2 />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function ExceptionTypeBadge({ type }: { type: ExceptionType }) {
  const { t } = useTranslation();
  const tones: Record<ExceptionType, string> = {
    unavailable: 'bg-ds-red-tint text-ds-red-ink',
    custom_hours: 'bg-ds-teal-tint text-ds-teal',
    meeting: 'bg-ds-teal-tint text-ds-teal',
    vacation: 'bg-ds-green-tint text-ds-green-ink',
    sick: 'bg-ds-amber-tint text-ds-amber-ink',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[type]}`}
    >
      {t(`users.exceptions.type_${type}`)}
    </span>
  );
}

function parseList(value: string): string[] {
  return value
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
}

function formatDateRange(start: string, end: string): string {
  if (start === end) return start;
  return `${start} – ${end}`;
}

function translateError(t: (key: string) => string, err: unknown): string {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (/last admin/i.test(msg)) return t('users.errors.last_admin');
  if (/forbidden/i.test(msg)) return t('users.errors.forbidden');
  if (/working_hours|HH MM|end after start/i.test(msg)) return t('users.errors.invalid_hours');
  if (/color/i.test(msg)) return t('users.errors.invalid_color');
  if (/invalid name/i.test(msg)) return t('users.errors.invalid_name');
  if (/must not contain CPR/i.test(msg)) return t('users.errors.signature_cpr');
  if (/signature is too long/i.test(msg)) return t('users.errors.signature_too_long');
  return t('users.errors.unknown');
}
